// src/services/RealTime/TicketsRealtimeService.ts
import RealtimeHub from './RealtimeHub'
import {
  normalizeScopes,
  makeRealtimeLogger
} from './realtimeCommon'

const WS_TOPIC = 'TICKET'

export default class TicketsRealtimeService {
  private static _unsubsByScope: Map<string, any> = new Map<string, any>()

  private static _scopes: Set<string> = new Set<string>();
  private static _started = false;
  private static _stopped = true;

  private static _flushTimer: any = null;
  private static _pendingUpserts: Set<string> = new Set<string>();

  private static _fetchTicket: any = null;
  private static _onUpsert: any = null;
  private static _onRemove: any = null;
  private static _onNeedsReload: any = null;
  private static _log: any = null;
  private static _logger: any = null;

  private static _realtimeUnsubReconnected: any = null;
  private static _realtimeUnsubResubscribed: any = null;

  static init ({
    fetchTicket,
    onUpsert,
    onRemove,
    onNeedsReload,
    log
  }: any = {}) {
    this._fetchTicket = fetchTicket
    this._onUpsert = onUpsert
    this._onRemove = onRemove
    this._onNeedsReload = onNeedsReload
    this._log = log

    this._logger = makeRealtimeLogger('[Realtime][TicketsRealtimeService]', this._log)

    this._debug('init', {
      hasFetchTicket: Boolean(this._fetchTicket),
      hasOnUpsert: Boolean(this._onUpsert),
      hasOnRemove: Boolean(this._onRemove),
      hasOnNeedsReload: Boolean(this._onNeedsReload),
      hasLog: Boolean(this._log)
    })
  }

  static setScopes (scopes: any) {
    this._debug('setScopes', { count: Array.isArray(scopes) ? scopes.length : 0 })

    const list: string[] = normalizeScopes(scopes)

    const next: Set<string> = new Set<string>(list)

    for (const s of this._scopes) {
      if (!next.has(s)) {
        this._unsubscribeScope(s)
      }
    }

    for (const s of next) {
      if (!this._scopes.has(s)) {
        this._subscribeScope(s)
      }
    }

    this._scopes = next
  }

  static async start (scopes: any = null) {
    this._stopped = false

    this._debug('start', { scopesProvided: Boolean(scopes) })

    if (scopes) {
      this.setScopes(scopes)
    }

    if (this._started) {
      return
    }

    this._started = true

    try {
      await RealtimeHub.start()
    } catch (e: any) {
      this._debug('RealtimeHub.start failed', e)
      this._notifyNeedsReload('hub_start_failed')
      return
    }

    if (!this._realtimeUnsubReconnected) {
      this._realtimeUnsubReconnected = RealtimeHub.onReconnected(() => {
        this._debug('onReconnected')
        this._notifyNeedsReload('reconnected')
      })
    }

    if (!this._realtimeUnsubResubscribed) {
      this._realtimeUnsubResubscribed = RealtimeHub.onResubscribed(() => {
        this._debug('onResubscribed')
        this._notifyNeedsReload('resubscribed')
      })
    }

    this._debug('subscribe all', { count: this._scopes.size })

    for (const s of this._scopes) {
      this._subscribeScope(s)
    }
  }

  static stop () {
    this._debug('stop')

    this._stopped = true
    this._started = false

    this._clearFlushTimer()
    this._pendingUpserts.clear()

    for (const s of this._scopes) {
      this._unsubscribeScope(s)
    }

    this._unsubsByScope.clear()

    if (this._realtimeUnsubReconnected) {
      try { this._realtimeUnsubReconnected() } catch { /* ignore */ }
      this._realtimeUnsubReconnected = null
    }

    if (this._realtimeUnsubResubscribed) {
      try { this._realtimeUnsubResubscribed() } catch { /* ignore */ }
      this._realtimeUnsubResubscribed = null
    }
  }

  private static _subscribeScope (scope: string) {
    if (!scope || this._stopped) {
      return
    }

    if (this._unsubsByScope.has(scope)) {
      return
    }

    this._debug('subscribe', { topic: WS_TOPIC, scope })

    RealtimeHub.subscribe({
      topic: WS_TOPIC,
      scope,
      parsed: true,
      handler: (payload: any) => {
        this._handleParsedMessage(payload)
      }
    }).then((unsub: any) => {
      if (this._stopped) {
        try { unsub() } catch { /* ignore */ }
        return
      }

      this._unsubsByScope.set(scope, unsub)
    }).catch((e: any) => {
      this._debug('subscribe failed', { topic: WS_TOPIC, scope, error: e })
      this._notifyNeedsReload('subscribe_failed')
    })
  }

  private static _unsubscribeScope (scope: string) {
    if (!scope) {
      return
    }

    const unsub = this._unsubsByScope.get(scope)
    if (!unsub) {
      return
    }

    this._unsubsByScope.delete(scope)

    this._debug('unsubscribe', { topic: WS_TOPIC, scope })

    try {
      unsub()
    } catch {
      // ignore
    }
  }

  private static _handleParsedMessage (data: any) {
    if (this._stopped) {
      return
    }

    if (!data) {
      return
    }

    const action = String(data.action || '').toUpperCase()
    const ticketId = data.ticketId

    if (!ticketId) {
      return
    }

    this._debug('message', { action, ticketId })

    if (action === 'REMOVE') {
      if (this._onRemove) {
        this._onRemove(ticketId, data)
      }
      return
    }

    if (action === 'UPSERT') {
      this._debug('UPSERT', ticketId)
      this._pendingUpserts.add(ticketId)
      this._debug('UPSERT', this._pendingUpserts)
      this._scheduleFlush()
    }
  }

  private static _scheduleFlush () {
    if (this._flushTimer) {
      this._debug('flush already scheduled')
      return
    }

    this._debug('schedule flush')

    this._flushTimer = setTimeout(() => {
      this._flushTimer = null
      this._flushUpserts().catch((e: any) => {
        this._debug('flush failed', e)
      })
    }, 50)
  }

  private static _clearFlushTimer () {
    if (this._flushTimer) {
      clearTimeout(this._flushTimer)
      this._flushTimer = null
    }
  }

  private static async _flushUpserts () {
    if (this._stopped) {
      return
    }

    const ids = Array.from(this._pendingUpserts)
    this._pendingUpserts.clear()

    this._debug('flush upserts', { count: ids.length })

    if (!ids.length) {
      return
    }

    if (!this._fetchTicket) {
      this._debug(
        'missing fetchTicket; triggering reload'
      )
      this._notifyNeedsReload('missing_fetch')
      return
    }

    for (const id of ids) {
      try {
        const ticket = await this._fetchTicket(id)
        if (ticket && this._onUpsert) {
          this._onUpsert(ticket)
        }
      } catch {
        if (this._onRemove) {
          this._onRemove(id, {
            action: 'REMOVE',
            ticketId: id,
            reason: 'FETCH_FAILED'
          })
        }
      }
    }
  }

  private static _notifyNeedsReload (reason: string) {
    if (this._stopped) {
      return
    }

    this._debug('needs reload', { reason })

    if (this._onNeedsReload) {
      this._onNeedsReload({ reason })
    }
  }

  private static _debug (...args: any[]) {
    if (this._logger) {
      this._logger(...args)
      return
    }

    try {
      // eslint-disable-next-line no-console
      console.log('[Realtime][TicketsRealtimeService]', ...args)
    } catch {
      // ignore
    }
  }
}
